import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

/* ─────────────────────────────────────────────────────────
   Full-Screen Loading Splash

   A branded full-screen loader with the GASIS logo icon
   and a flame-style fill animation. Shown on route changes.
   ───────────────────────────────────────────────────────── */

/* Inline flame SVG — just the flame paths, no white circle background */
function FlameIcon({
  color,
  className,
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 57 57"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M21.5883 22.1011C22.0303 16.4895 26.8908 13.7241 29.2655 13.043C29.0887 13.3521 28.6394 15.0496 28.437 15.8596C27.7743 18.7319 30.1854 21.498 31.5856 22.7088C33.8144 24.6362 35.8383 26.3172 36.3354 26.9616C40.4447 31.3361 39.3733 36.2956 38.3236 38.2287C35.9378 43.5756 30.6281 44.3596 28.2713 44.0833C32.9994 42.8462 34.5496 38.0814 34.7336 35.8541C35.5731 31.8771 31.5855 26.9065 29.4867 24.9178C26.2831 21.7379 26.0991 18.8241 26.2831 17.9034C23.4993 20.5105 25.602 26.2433 27.0009 28.7841C28.3264 31.4796 28.0689 34.0682 27.7743 35.0256C26.3604 40.7696 20.2628 42.3163 17.3906 42.3714C24.4161 38.3505 23.9263 31.8955 22.8032 29.1706C21.5661 25.9892 21.4777 23.132 21.5883 22.1011Z"
        fill={color}
      />
      <path
        d="M18.839 30.4314C18.8245 33.5535 15.4583 34.7788 15.4583 39.1263C14.4915 37.6771 13.5254 36.228 13.5254 34.7788C13.5254 29.9483 16.1825 29.9483 14.4915 24.6348C16.1825 26.0839 18.8491 28.2409 18.839 30.4314Z"
        fill={color}
      />
      <path
        d="M36.7129 19.8045C36.7129 18.1943 38.001 16.3158 38.6451 15.457C37.679 18.9994 40.5773 19.1604 40.5773 21.7367C40.5773 22.7672 39.6112 24.474 38.6451 25.118C38.6451 22.5418 36.7129 22.3808 36.7129 19.8045Z"
        fill={color}
      />
    </svg>
  );
}

function LoadingSplash({
  onDone,
  accentColor,
}: {
  onDone: () => void;
  accentColor: string;
}) {
  const [phase, setPhase] = useState<"filling" | "done">("filling");

  useEffect(() => {
    // Fill animation runs, then we fade out and signal done
    const fillTimer = setTimeout(() => setPhase("done"), 600);
    const doneTimer = setTimeout(onDone, 900);
    return () => {
      clearTimeout(fillTimer);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  return (
    <div
      className={`fixed inset-0 z-[3000] flex items-center justify-center bg-white transition-opacity duration-300 ${
        phase === "done" ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      <div className="flex flex-col items-center">
        {/* Logo with spinning border ring + flame fill */}
        <div className="relative flex items-center justify-center w-[140px] h-[140px] md:w-[160px] md:h-[160px]">
          {/* Spinning border ring */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: "3px solid #e8e9ed",
              borderTopColor: accentColor,
              borderRightColor: accentColor,
              animation: "spinRing 0.7s linear infinite",
            }}
          />

          {/* Logo container */}
          <div className="relative w-[80px] h-[80px] md:w-[96px] md:h-[96px]">
            {/* Faint base */}
            <FlameIcon
              color={accentColor}
              className="absolute inset-0 w-full h-full opacity-15"
            />
            {/* Colored flame revealed by rising clip */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{
                animation:
                  "flameFill 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards",
              }}
            >
              <FlameIcon color={accentColor} className="w-full h-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes flameFill {
          0% { clip-path: inset(100% 0 0 0); }
          100% { clip-path: inset(0 0 0 0); }
        }
        @keyframes spinRing {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Root Page Transition Layout

   Wraps all routes with:
   - Scroll-to-top on navigation
   - Full-screen branded loading splash between pages
   - Smooth fade-in for page content
   ───────────────────────────────────────────────────────── */

/* Scrollbar theme per route section */
const SCROLLBAR_THEMES: { prefix: string; color: string; hover: string }[] = [
  { prefix: "/business", color: "#F24D2B", hover: "#3d405e" },
  { prefix: "/partners", color: "#D48C07", hover: "#0a5a5a" },
  { prefix: "/privacy-policy", color: "#82716E", hover: "#1a3f8a" },
  { prefix: "/sla", color: "#122d63", hover: "#1a3f8a" },
];
const DEFAULT_THEME = { color: "#f24d2b", hover: "#d93d1e" };

function getScrollbarTheme(pathname: string) {
  for (const t of SCROLLBAR_THEMES) {
    if (pathname.startsWith(t.prefix)) return t;
  }
  return DEFAULT_THEME;
}

export function PageTransition() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const prevPathname = useRef(location.pathname);
  const isFirstRender = useRef(true);

  // Dynamic scrollbar color based on route
  useEffect(() => {
    const theme = getScrollbarTheme(location.pathname);
    document.documentElement.style.setProperty(
      "--scrollbar-color",
      theme.color
    );
    document.documentElement.style.setProperty(
      "--scrollbar-hover",
      theme.hover
    );
  }, [location.pathname]);

  useEffect(() => {
    // Skip the initial render — no splash on first page load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevPathname.current = location.pathname;
      return;
    }

    // Only trigger splash when navigating to a different page
    if (prevPathname.current !== location.pathname) {
      prevPathname.current = location.pathname;
      setLoading(true);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname]);

  return (
    <>
      {loading && (
        <LoadingSplash
          onDone={() => setLoading(false)}
          accentColor={getScrollbarTheme(location.pathname).color}
        />
      )}
      <div key={location.pathname} className="animate-pageEnter">
        <Outlet />
      </div>
    </>
  );
}
